import { isIOSNative } from "./platform";

const parsePx = (value: string | null | undefined): number => {
  if (!value) return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const applyToElement = (el: HTMLElement, newTop: number) => {
  el.style.setProperty("--ion-safe-area-top", `${newTop}px`);
  el.style.setProperty("--offset-top", `${newTop}px`);
};

const revertStyle = (el: HTMLElement, datasetKey: string, property: string) => {
  const base = el.dataset[datasetKey];
  if (typeof base === "string") {
    el.style.setProperty(property, base);
  } else {
    el.style.removeProperty(property);
  }
};

const ensureBaseStyle = (
  el: HTMLElement,
  datasetKey: string,
  property: string,
  fallback: string = ""
): string => {
  const existing = el.dataset[datasetKey];
  if (typeof existing === "string") {
    return existing;
  }
  const computed = getComputedStyle(el).getPropertyValue(property) || fallback;
  el.dataset[datasetKey] = computed;
  return computed;
};

export const applyGlobalSafeAreaOffset = (
  offsetPx: number,
  reason: string = "unknown"
): void => {
  if (!isIOSNative()) {
    console.debug("[SafeAreaOffset] Skipping (not iOS native)", { reason, offsetPx });
    return;
  }
  if (typeof document === "undefined") {
    console.debug("[SafeAreaOffset] Skipping (no document)", { reason, offsetPx });
    return;
  }

  const root = document.documentElement;
  const body = document.body;
  const computedTop = parsePx(
    getComputedStyle(root).getPropertyValue("--ion-safe-area-top")
  );
  const ionAppEl = document.querySelector<HTMLElement>("ion-app");
  const ionAppTop = ionAppEl
    ? parsePx(getComputedStyle(ionAppEl).getPropertyValue("--ion-safe-area-top"))
    : null;
  const previouslyApplied = parsePx(root.dataset.safeAreaExtraTop);
  const baseTop = Math.max(computedTop - previouslyApplied, 0);
  const newTop = baseTop + offsetPx;

  root.dataset.safeAreaExtraTop = `${offsetPx}`;
  if (offsetPx > 0) {
    body.classList.add("ios-safearea-offset");
    body.style.setProperty("--safe-area-extra-top", `${offsetPx}px`);
  } else {
    body.classList.remove("ios-safearea-offset");
    body.style.removeProperty("--safe-area-extra-top");
  }
  console.debug("[SafeAreaOffset] Applying", {
    reason,
    computedTop,
    ionAppTop,
    previouslyApplied,
    baseTop,
    offsetPx,
    newTop,
  });
  applyToElement(root, newTop);
  applyToElement(body, newTop);

  let ionAppTransform: string | null = null;
  let ionAppHeight: string | null = null;
  let ionAppMinHeight: string | null = null;

  if (ionAppEl) {
    applyToElement(ionAppEl, newTop);
    // Revert any earlier padding/margin adjustments
    revertStyle(ionAppEl, "safeAreaBasePaddingTop", "padding-top");
    revertStyle(ionAppEl, "safeAreaBaseMarginBottom", "margin-bottom");

    const baseTransform = ensureBaseStyle(
      ionAppEl,
      "safeAreaBaseTransform",
      "transform",
      "none"
    );
    const baseHeight = ensureBaseStyle(
      ionAppEl,
      "safeAreaBaseHeight",
      "height",
      "100%"
    );
    const baseMinHeight = ensureBaseStyle(
      ionAppEl,
      "safeAreaBaseMinHeight",
      "min-height",
      "100%"
    );

    ionAppTransform =
      offsetPx !== 0
        ? `${baseTransform && baseTransform !== "none" ? `${baseTransform} ` : ""}translateY(${offsetPx}px)`
        : baseTransform;
    ionAppHeight =
      offsetPx !== 0
        ? `calc(100% - ${offsetPx}px)`
        : baseHeight;
    ionAppMinHeight =
      offsetPx !== 0
        ? `calc(100% - ${offsetPx}px)`
        : baseMinHeight;

    if (ionAppTransform && ionAppTransform.trim().length > 0 && ionAppTransform !== "none") {
      ionAppEl.style.transform = ionAppTransform;
    } else {
      ionAppEl.style.removeProperty("transform");
    }

    if (ionAppHeight && ionAppHeight.trim().length > 0) {
      ionAppEl.style.height = ionAppHeight;
    } else {
      ionAppEl.style.removeProperty("height");
    }
    if (ionAppMinHeight && ionAppMinHeight.trim().length > 0) {
      ionAppEl.style.minHeight = ionAppMinHeight;
    } else {
      ionAppEl.style.removeProperty("min-height");
    }
  }

  requestAnimationFrame(() => {
    const postRoot = parsePx(
      getComputedStyle(root).getPropertyValue("--ion-safe-area-top")
    );
    const postIonApp = ionAppEl
      ? parsePx(getComputedStyle(ionAppEl).getPropertyValue("--ion-safe-area-top"))
      : null;
    console.debug("[SafeAreaOffset] Post-apply verification", {
      reason,
      postRoot,
      postIonApp,
      dataset: root.dataset.safeAreaExtraTop,
      ionAppTransform,
      ionAppHeight,
      ionAppMinHeight,
    });
  });
};
